import { differenceInMinutes, parseISO } from 'date-fns';
import { Attendance } from '../../../interfaces/attendance.interface';
import { LeaveBalance } from '../../../interfaces/dashboard.interface';
import { LeaveApplication, LeaveStatus } from '../../../interfaces/leave.interface';
import { formatDate } from '../../../i18n';

/** A calendar date ('YYYY-MM-DD', read as a local day) in the current language, e.g. '1 Oct 2026'. */
export function formatDay(iso: string): string {
    return formatDate(parseISO(iso));
}

/** A time of day (ISO instant) in the current language, e.g. '09:05'. */
export function formatClock(iso: string): string {
    return formatDate(iso, { hour: '2-digit', minute: '2-digit' });
}

/** Today as a local YYYY-MM-DD string. */
export function localIsoDate(d: Date = new Date()): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Whole-day distance between two local YYYY-MM-DD strings (b - a). */
export function daysBetween(aIso: string, bIso: string): number {
    const [ay, am, ad] = aIso.split('-').map(Number);
    const [by, bm, bd] = bIso.split('-').map(Number);
    return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86400000);
}

/** Minutes on site for a punch: the recorded worked minutes, else clock-out minus clock-in. */
export function attendanceMinutes(a: Attendance): number | null {
    if (a.workedMinutes != null && a.workedMinutes > 0) return a.workedMinutes;
    if (a.startTime && a.endTime) {
        const mins = differenceInMinutes(parseISO(a.endTime), parseISO(a.startTime));
        return mins > 0 ? mins : null;
    }
    return null;
}

export interface MonthSummary {
    daysPresent: number;
    lateDays: number;
    /** One entry per day with a completed punch pair, oldest first; value = hours (1 dp). */
    hoursPerDay: { label: string; value: number }[];
}

export function summarizeMonth(rows: Attendance[]): MonthSummary {
    const byDate = new Map<string, Attendance[]>();
    rows.forEach(r => byDate.set(r.date, [...(byDate.get(r.date) ?? []), r]));
    const dates = Array.from(byDate.keys()).sort();
    const hoursPerDay = dates.flatMap(d => {
        const mins = (byDate.get(d) ?? []).reduce((s, r) => s + (attendanceMinutes(r) ?? 0), 0);
        return mins > 0 ? [{ label: d.slice(8), value: Math.round((mins / 60) * 10) / 10 }] : [];
    });
    return {
        daysPresent: dates.length,
        lateDays: dates.filter(d => (byDate.get(d) ?? []).some(r => r.isLate === true)).length,
        hoursPerDay,
    };
}

/** Balances worth showing: a type with nothing allocated (e.g. Maternity for men) is noise. */
export function activeBalances(balances: LeaveBalance[]): LeaveBalance[] {
    return balances.filter(b => b.allocated > 0);
}

export function isApprovedOn(app: LeaveApplication, todayIso: string): boolean {
    return app.leaveStatus === LeaveStatus.Approved && app.startDate <= todayIso && app.endDate >= todayIso;
}
